import React, { useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usePolicy } from '../../contexts/PolicyContext';
import PolicyAcceptanceModal from './PolicyAcceptanceModal';
import LoadingIndicator from '../loading-indicator/LoadingIndicator';
import { createLogger } from '../../utils/logger';
import './PolicyAcceptanceModal.scss';

const logger = createLogger('PolicyGuard');

interface PolicyGuardProps {
  children: React.ReactNode;
}

const PolicyGuard: React.FC<PolicyGuardProps> = ({ children }) => {
  const { currentUser, loading, logout } = useAuth() as {
    currentUser: { id: string } | null;
    loading: boolean;
    logout: () => Promise<void>;
  };
  const {
    isSkippedPath,
    checking,
    checkError,
    retryCount,
    maxRetries,
    modalOpen,
    saving,
    acceptPolicies,
    retry,
  } = usePolicy();

  const handleLogout = useCallback(async () => {
    try {
      await logout();
    } catch (err) {
      logger.error('[PolicyGuard] Logout failed:', err);
    }
  }, [logout]);

  if (!isSkippedPath && currentUser && (loading || checking || modalOpen)) {
    return (
      <>
        <LoadingIndicator message="" />

        {modalOpen && (
          <PolicyAcceptanceModal
            isOpen={modalOpen}
            onAccept={acceptPolicies}
            onLogout={handleLogout}
            isSaving={saving}
          />
        )}
      </>
    );
  }

  return (
    <>
      {children}

      {/* Error overlay — shown when policy status could not be verified */}
      {!isSkippedPath && currentUser && checkError && (
        <div className="policy-blocked-overlay" role="alertdialog" aria-live="assertive">
          <p className="policy-blocked-overlay__message">
            {retryCount >= maxRetries
              ? 'We were unable to verify your policy acceptance status. Please log out and try again later.'
              : 'We couldn\u2019t verify your policy acceptance status. Please try again to continue.'}
          </p>
          <div className="policy-blocked-overlay__actions">
            {retryCount < maxRetries && (
              <button className="policy-blocked-overlay__review-btn" onClick={retry} type="button">
                Try Again
              </button>
            )}
            <button className="policy-blocked-overlay__logout-link" onClick={handleLogout} type="button">
              Log me out
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default PolicyGuard;
